import { useEffect, useRef, useCallback } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const STORAGE_KEY = 'arys_last_activity';
// Ayni sekmede her mousemove'da localStorage'a yazmamak icin throttle -
// uyari gosterildikten sonra (warnedRef) throttle'i atliyoruz ki kullanici
// modal acikken herhangi bir harekette hemen "hala buradayim" sayilsin.
const WRITE_THROTTLE_MS = 5000;

// timeoutMs suresince hic aktivite olmazsa onTimeout, ondan warningMs once
// onWarn cagrilir. Aktivite localStorage'a yaziliyor ve `storage` event'i
// ile diger acik sekmelere de yayiliyor - bir sekmede calisirken digerinde
// oturum kapanmasin diye.
export default function useIdleTimeout({ enabled, timeoutMs, warningMs, onWarn, onTimeout, onReset }) {
  const warnTimerRef    = useRef(null);
  const timeoutTimerRef = useRef(null);
  const lastWriteRef    = useRef(0);
  const warnedRef       = useRef(false);

  const clearTimers = useCallback(() => {
    if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
  }, []);

  const scheduleFrom = useCallback((lastActivity) => {
    clearTimers();
    warnedRef.current = false;
    onReset?.();

    const now = Date.now();
    const warnDelay    = (lastActivity + (timeoutMs - warningMs)) - now;
    const timeoutDelay = (lastActivity + timeoutMs) - now;

    if (timeoutDelay <= 0) {
      onTimeout();
      return;
    }

    // onWarn'a kalan gercek sureyi (ms) de veriyoruz - normal akista bu her
    // zaman ~warningMs'e esit olur, ama sekme uzun sure arka planda/uykuda
    // kalip geri geldiginde warn ve timeout esikleri ayni anda gecilmis
    // olabilir - o durumda geri sayim sabit warningMs degil, gercekten
    // kalan (daha kisa) sureyi göstermeli.
    if (warnDelay <= 0) {
      warnedRef.current = true;
      onWarn(Math.max(0, timeoutDelay));
    } else {
      warnTimerRef.current = setTimeout(() => {
        warnedRef.current = true;
        onWarn(Math.max(0, (lastActivity + timeoutMs) - Date.now()));
      }, warnDelay);
    }

    timeoutTimerRef.current = setTimeout(onTimeout, timeoutDelay);
  }, [timeoutMs, warningMs, onWarn, onTimeout, onReset, clearTimers]);

  const reset = useCallback(() => {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, String(now));
    scheduleFrom(now);
  }, [scheduleFrom]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Sayfa yeniden yuklendiginde baska bir sekmeden kalma aktivite varsa
    // onu baz al, yoksa simdiden baslat.
    const stored  = Number(localStorage.getItem(STORAGE_KEY));
    const initial = stored && !Number.isNaN(stored) ? stored : Date.now();
    scheduleFrom(initial);

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastWriteRef.current < WRITE_THROTTLE_MS && !warnedRef.current) return;
      lastWriteRef.current = now;
      reset();
    };

    // Baska bir sekmede aktivite olunca bu sekmenin sayaci da sifirlansin.
    const handleStorage = (e) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      scheduleFrom(Number(e.newValue));
    };

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
    window.addEventListener('storage', handleStorage);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      window.removeEventListener('storage', handleStorage);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { reset };
}
